import React from 'react';
import { alpha, Box, Button, Paper, styled, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSnackbar } from 'notistack';
import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';

const StyledPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.purple.core, 0.1),
    borderColor: theme.palette.purple.tertiary
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.purple.core
}));

const StyledButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.purple.core,
    color: theme.palette.common.white,
    borderColor: theme.palette.purple.core,
    textTransform: 'none',
    fontWeight: 700,
    padding: theme.spacing(1, 2),
    '&:hover': {
        backgroundColor: alpha(theme.palette.purple.core, 0.9),
        borderColor: theme.palette.purple.core,
        color: theme.palette.common.white
    }
}));

export const UnlockPremiumUTMBanner = ({ direction = 'row' }: { direction?: 'row' | 'column' }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { logUserEvent } = useGAHelpers();

    const handleUnlockUTMTracking = React.useCallback(() => {
        logUserEvent(EGAEventName.CampaignLinksPageIAmInterestedClick);
        enqueueSnackbar("Thanks — we'll be in touch with more information.", { variant: 'success' });
    }, [logUserEvent, enqueueSnackbar]);

    return (
        <StyledPaper elevation={0} variant="outlined">
            <Box
                display="flex"
                alignItems={direction === 'row' ? 'center' : 'flex-start'}
                gap={2}
                padding={2}
                flexDirection={direction}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems={direction === 'row' ? 'center' : 'flex-start'}
                    gap={1}
                    flex={1}
                >
                    <Box paddingTop={direction === 'row' ? 0 : 1}>
                        <AutoAwesomeIcon sx={{ fontSize: 24, color: 'purple.core' }} />
                    </Box>
                    <Box flex={1} display="flex" flexDirection="column">
                        <Typography variant="body1" color="purple.core">
                            <b>
                                Upgrade to create more links, compare click performance, and unlock UTM
                                tracking
                            </b>
                        </Typography>
                        <StyledSubtitle variant="body2">
                            Tag every link with source, medium, and campaign so you can compare what&apos;s
                            actually driving clicks and app installs, and double down on what works. Track web
                            ordering link clicks and assign unique table numbers for more targeted in-store
                            ordering journeys.
                        </StyledSubtitle>
                    </Box>
                </Box>
                <StyledButton
                    onClick={handleUnlockUTMTracking}
                    variant="contained"
                    sx={{ marginLeft: direction === 'row' ? 0 : 4 }}
                    color="primary"
                >
                    Unlock Advanced Tracking
                </StyledButton>
            </Box>
        </StyledPaper>
    );
};
